import type { PropsWithChildren, ReactElement } from 'react';

import { type IBaseTextProps, Text } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { useFormatPunchOutStatus } from '@webapp/hooks/useFormatPunchOutStatus'
import { type OrderItem } from '@webapp/models'
import { type PunchOutStatus } from '@webapp/models/enums'
import messages from './messages'
import { PunchOutOrderInfoTooltip } from '../PunchOutOrderInfo'

export interface IPunchOutStatusLabelProps {
  status: PunchOutStatus
  submittedDate: OrderItem['submitted_date']
  isFailedPunchoutOrder?: OrderItem['punchout_is_retriable']
  children?: (props: IBaseTextProps) => ReactElement
  textColor?: IBaseTextProps['color']
}

export const PunchOutStatusLabel = ({
  status,
  submittedDate,
  isFailedPunchoutOrder = false,
  children,
  textColor = 'danger',
}: PropsWithChildren<IPunchOutStatusLabelProps>) => {
  const formatPunchOutStatus = useFormatPunchOutStatus()
  const intl = useProcurifyIntl()

  const textProps: PropsWithChildren<IBaseTextProps> = {
    color: textColor,
    children: isFailedPunchoutOrder
      ? intl.formatMessage(messages.orderFailedText)
      : formatPunchOutStatus(status),
  }

  let label

  if (children) {
    label = children(textProps)
  } else {
    label = <Text {...textProps} />
  }

  return (
    <PunchOutOrderInfoTooltip
      isFailedPunchoutOrder={isFailedPunchoutOrder}
      confirmationStatus={status}
      submittedDate={submittedDate}
    >
      {label}
    </PunchOutOrderInfoTooltip>
  )
}

PunchOutStatusLabel.displayName = 'PunchOutStatusLabel'
